import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { AppConfig } from '../../app.config';
import { context } from 'bookingbug-core-js';
import { environment } from '../../../environments/environment';
import { firstValueFrom, Observable, throwError } from 'rxjs';
import { DatastoreService } from '../datastore.service';
import { catchError, map } from 'rxjs/operators';
@Injectable({
	providedIn: 'root',
})
export class ApiService {
	private jrniCore = AppConfig.settings.core;
	private core = AppConfig.settings.addressApi;
	private jrniEndpoint = AppConfig.settings.apiServer;
	private token =
		'0edcef2724f509e18318463fdc8c33b611c0679fd63d6e573036822d45a571c0';

	public apiServer;
	public blApiToken;
	public blApi;
	public frontSheetEmail;
	public configCache: any = {};

	constructor(
		private http: HttpClient,
		private datastoreService: DatastoreService
	) {
		if (environment.production) {
			this.apiServer = 'https://api.bookinglab.co.uk';
			// TODO(SRPO-169): provision a production client token for solihull:37016 via
			// token-maker before go-live (staging branch below already uses Solihull's own token).
			this.blApiToken =
				'hsvofperchyuduu647sopkm42590925403778840x6v44qauxltakhp368972293';
			this.blApi = 'https://pnwnvb64o6.execute-api.eu-west-2.amazonaws.com/1';
			console.log('Produuction api');
			this.frontSheetEmail = 'front.sheets@essex.gov.uk';
		} else {
			this.apiServer = 'https://staging.bookinglab.co.uk';
			this.blApiToken =
				'24ubmv3f94859044007871611t93lscncjl9kd8losaakx4xr2je70achp3e193s';
			this.blApi = 'https://m5nius1kad.execute-api.eu-west-2.amazonaws.com/1';
			console.log('Staging api');
			this.frontSheetEmail = 'chad.duggan@bookinglab.co.uk';
		}
	}


	async addMetadataToBooking(companyId: number, bookingId: number, metadataType: string, data: any) {
		try {
			const headers = {
				'Content-Type': 'application/json',
				clienttoken: `${this.blApiToken}`,
			};

			return await this.http.post(`${this.blApi}/company/${companyId}/booking/${bookingId}/meta/${metadataType}`, data, { headers }).toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	getAppConfig(): { blApiToken: string; apiBaseUrl: string; company_id: string; customer_name: string; } {
		return {
			blApiToken: this.blApiToken,
			apiBaseUrl: this.blApi,
			company_id: AppConfig.settings.core.company_id,
			customer_name: AppConfig.settings.env.customer_name,
		}
	}

	setCitizenshipToken() {
		console.log('Citizenship token set');
		this.blApiToken =
			'24ubmv3f94859044007871611t93lscncjl9kd8losaakx4xr2je70achp3e193s';
	}

	get(postcode) {
		const key = this.core.key;
		return this.http.get(
			`https://wcc-dev-apim-external.azure-api.net/locations/v1/postcode?postcode=${postcode}&national=true`, { headers: { 'Ocp-Apim-Subscription-Key': '1f4dc84e8a764428812136e8cefb0cd6' } }
		);
	}

	async getExtraItemProperties(bookingQuestionCode, companyId, serviceId) {
		try {
			const headers = {
				'Content-Type': 'application/json',
				clienttoken: `${this.blApiToken}`,
			};

			return await this.http
				.get(
					`${this.blApi}/company/${companyId}/extra-item/${bookingQuestionCode}/service/${serviceId}`,
					{ headers }
				)
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async lookupReference(table: any, field: any, reference: any, referenceExtra: any = false, field_extra: any = false) {
		try {
			const headers = {
				'Content-Type': 'application/json',
				clienttoken: `${this.blApiToken}`,
			};

			const bodyData = {
				client: `${AppConfig.settings.env.customer_name}`,
				reference: `${reference}`,
				table: `${table}`,
				field: `${field}`
			};

			if (referenceExtra && field_extra) {
				bodyData["referenceExtra"] = referenceExtra
				bodyData["field_extra"] = field_extra
			}

			return await this.http
				.post(`${this.blApi}/eligibility_reference_lookup`, bodyData, {
					headers,
				})
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async updateReference(referencebj: any, booking: any, selectedAddress: any, iteration: any) {
		try {
			const headers = {
				'Content-Type': 'application/json',
				clienttoken: `${this.blApiToken}`,
			};

			// Get answers from the new schema-based form builder storage
			const formBuilderAnswers = JSON.parse(sessionStorage.getItem('form_builder_answers') || '{}');

			// key is what is passed into data, field is the schema field name, person is which iteration to match (1 or 2) or * for all
			const questionKeys = [
				{ key: "numberOfGuests", field: "number_of_guests_non_applicants_1", person: "*" },
				{ key: "numberOfGuests", field: "number_of_guests_non_applicants", person: "*" },
				{ key: "oathQuestion", field: "are_attendees_swearing_or_affirming_oath", person: 1 },
				{ key: "oathQuestion", field: "are_attendees_swearing_or_affirming_oath_1", person: 1 },
				{ key: "oathQuestion", field: "are_attendees_swearing_or_affirming_oath_2", person: 1 }
			];

			const bodyData = {
				home_office: referencebj.home_office,
				used: true,
				id: referencebj.id,
				name: referencebj.name,
				poll_date: referencebj.poll_date,
				required_by_date: referencebj.required_by_date,
				minor: referencebj.minor,
				extra_info: referencebj.extra_info,
				local_authority: referencebj.local_authority,
				function: 'put',
				client: `${AppConfig.settings.env.customer_name}`,
				booking_id: booking.id,
				booking_datetime: booking.datetime.split("+")[0],
				email: booking.email,
				telephone: referencebj.telephone,
				address: selectedAddress,
				data: {}
			};

			// filter questions based on iteration
			const filteredQuestions = questionKeys.filter(q => q.person === '*' || q.person === iteration);

			// populate data by looking up answers from schema storage by field name
			filteredQuestions.forEach(({ key, field }) => {
				let value = null;
				// Search across all form IDs for the field
				for (const formId of Object.keys(formBuilderAnswers)) {
					if (formBuilderAnswers[formId]?.[field] !== undefined) {
						value = formBuilderAnswers[formId][field];
						break;
					}
				}
				// Only set if we found a value, or if key doesn't exist yet (avoids overwriting with null/empty)
				if (value || bodyData.data[key] === undefined) {
					bodyData.data[key] = value;
				}
			});

			// make API call
			return await this.http
				.post(`${this.blApi}/citizenship`, bodyData, { headers })
				.toPromise();

		} catch (error) {
			return (error as Error).message;
		}
	}


	async getWeekOfBookings(address, company) {
		const headers = {
			'Content-Type': 'application/json',
			'X-BL-TOKEN': `${this.token}`,
		};
		const body = {
			host: `${context.apiUrl}`,
			company_id: `${company.id}`,
			limit: `${company.extra.booking_limit_days}`,
			address: `${address}`,
		};

		return await this.http
			.post(`${this.apiServer}/weeklyLimit`, body, { headers })
			.toPromise();
	}

	async sendMessage(message) {
		const headers = {
			'Content-Type': 'application/json',
			'X-BL-TOKEN': `${this.token}`,
		};
		const body = {
			slot: '52500',
			to: 'samuel.green@bookinglab.com',
			from: 'Online form submission <noreply@essex.gov.uk>',
			subject: 'Online ceremony booking contact form',
			type: message,
		};

		return await this.http
			.post(`${this.apiServer}/email/liquid`, body, { headers })
			.toPromise();
	}

	async checkEmailNumber(email, mobile, company) {
		const headers = {
			'Content-Type': 'application/json',
			'X-BL-TOKEN': `${this.token}`,
		};
		const body = {
			host: `${context.apiUrl}`,
			company_id: `${company.id}`,
			email: email ? email : mobile,
			searchType: 'elastic',
		};

		return await this.http
			.post(`${this.apiServer}/findElasticClient`, body, { headers })
			.toPromise();
	}

	async getBookingsForUser(company, parent, email) {
		const headers = {
			'Content-Type': 'application/json',
			'X-BL-TOKEN': `${this.token}`,
		};
		const body = {
			host: `${context.apiUrl}`,
			company_id: `${company.id}`,
			parent_id: `${parent.id}`,
			email: `${email}`,
		};

		return await this.http
			.post(`${this.apiServer}/findElasticClient`, body, { headers })
			.toPromise();
	}

	async checkForDoubleBooking(company, slot, email = null, mobile = null) {
		const headers = {
			'Content-Type': 'application/json',
			'X-BL-TOKEN': `${this.token}`,
		};
		const body = {
			host: `${context.apiUrl}`,
			company_id: `${company.id}`,
			current_date: slot,
		};

		if (email) {
			body['email'] = email;
		} else {
			body['mobile'] = mobile;
		}

		return this.http
			.post(`${this.apiServer}/unavailableTimes`, body, { headers })
			.toPromise();
	}

	async getTimes(
		company_id: number,
		service_id: number,
		start_date: string,
		end_date: string,
		resource_id: number,
		duration: number
	) {
		try {
			const headers: HttpHeaders = new HttpHeaders({
				'App-Id': this.jrniCore.app_id,
				'App-Key': this.jrniCore.app_key,
			});

			const url = `${this.jrniEndpoint.api_url}/api/v5/${company_id}/times?only_available=true&service_id=${service_id}&start_date=${start_date}&end_date=${end_date}&duration=${duration}&resource_id=${resource_id}&person_id=${this.datastoreService.staff_set}`;
			console.log(headers);
			console.log(url);
			return await this.http.get(url, { headers }).toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async checkSlotAvailability(company, slot) {
		const headers = {
			'Content-Type': 'application/json',
			'X-BL-TOKEN': `${this.token}`,
		};
		const body = {
			host: `${context.apiUrl}`,
			company_id: `${company.id}`,
			slot: slot,
		};

		return this.http
			.post(`${this.apiServer}/slotAvailability`, body, { headers })
			.toPromise();
	}

	getServiceByAdmin(parent_id, company, service_id, resource_id) {
		const headers = {
			'Content-Type': 'application/json',
			'X-BL-TOKEN': `${this.token}`,
		};
		const body = {
			host: `${context.apiUrl}`,
			parent_id: parent_id,
			service_id: service_id,
			company_id: `${company.id}`,
			resource_id: resource_id,
		};
		return this.http
			.post(`${this.apiServer}/getServiceByAdmin`, body, { headers })
			.toPromise();
	}
	async findPurchase(company, purchaseId) {
		const headers = {
			'Content-Type': 'application/json',
			'X-BL-TOKEN': `${this.token}`,
		};
		const body = {
			host: `${context.apiUrl}`,
			company_id: `${company.id}`,
			purchase_id: purchaseId,
		};

		return this.http
			.post(`${this.apiServer}/findPurchase`, body, { headers })
			.toPromise();
	}

	async getService(company, service_id) {
		const headers = {
			'Content-Type': 'application/json',
			'X-BL-TOKEN': `${this.token}`,
		};
		const body = {
			host: `${context.apiUrl}`,
			company_id: `${company.id}`,
			service_id: service_id,
		};

		return this.http
			.post(`${this.apiServer}/getService`, body, { headers })
			.toPromise();
	}

	async getConfig(company: any = undefined) {
		// mini cache for the config
		if (Object.keys(this.configCache).length > 0) {
			console.log('Returning cached config');
			return this.configCache;
		}

		console.log('Fetching config for company:', company);
		const headers = {
			'Content-Type': 'application/json',
			clienttoken: `${this.blApiToken}`,
		};
		const body = {
			client: `${AppConfig.settings.env.customer_name}`,
			company: `${company}`,
		};

		const result = await this.http
			.post(`${this.blApi}/config`, body, { headers })
			.toPromise();

		this.configCache = result;
		return result;
	}

	async addNoteToBooking(company: any, bookingId: number, note: string) {
		const headers = {
			'Content-Type': 'application/json',
			'X-BL-TOKEN': `${this.token}`,
		};
		const body = {
			host: `${context.apiUrl}`,
			company_id: `${company.id}`,
			bookingId: `${bookingId}`,
			note: `booking`,
		};

		return await this.http
			.post(`${this.apiServer}/addNote`, body, { headers })
			.toPromise();
	}

	async updatePrice(
		company: any,
		bookingId: number,
		purchaseId: string,
		price: string,
		paid: string
	) {
		const headers = {
			'Content-Type': 'application/json',
			'X-BL-TOKEN': `${this.token}`,
		};
		const body = {
			host: `${context.apiUrl}`,
			company_id: `${company.id}`,
			booking_id: `${bookingId}`,
			purchase_id: `${purchaseId}`,
			price: `${price}`,
			pay: `${paid}`,
		};

		return await this.http
			.post(`${this.apiServer}/updatePrice`, body, { headers })
			.toPromise();
	}

	// async sendFrontSheetEmail(booking) {
	// 	try {
	// 		const headers = {
	// 			'Content-Type': 'application/json',
	// 			'X-BL-TOKEN': `${this.token}`,
	// 		};
	// 		const senderAddress = 'notifications@bookinglab.co.uk';
	// 		let subject = 'Westminster Registration Service - Ceremonies Front Sheet';
	// 		const data: EmailType = {
	// 			host: `${context.apiUrl}`,
	// 			companyid: '37001',
	// 			slotid: booking.id,
	// 			recipient: this.frontSheetEmail,
	// 			type: 'frontsheet-email',
	// 			senderAddress,
	// 			subject,
	// 		};
	// 		return await this.http
	// 			.post(`${this.apiServer}/sendCommunicationEmail`, data, { headers })
	// 			.toPromise();
	// 	} catch (error) {
	// 		return (error as Error).message;
	// 	}
	// }

	// async sendEmail(type, bookingData, recipient, company) {
	// 	try {
	// 		const headers = {
	// 			'Content-Type': 'application/json',
	// 			'X-BL-TOKEN': `${this.token}`,
	// 		};
	// 		const senderAddress = 'notifications@bookinglab.co.uk';
	// 		let subject = '';
	// 		if (type === 'CANCEL') {
	// 			subject = 'Essex Registration Service - Booking cancelled';
	// 		} else if (type === 'AMEND') {
	// 			subject = 'Essex Registration Service - Booking amended';
	// 		} else if (type === 'MOVED') {
	// 			subject = 'Essex Registration Service - Booking moved';
	// 		} else if (type === 'CONFIRMED') {
	// 			subject = 'Essex Registration Service - Booking Confirmation';
	// 		}
	// 		const data: EmailType = {
	// 			host: `${context.apiUrl}`,
	// 			companyid: '37001',
	// 			slotid: bookingData.id,
	// 			recipient: recipient,
	// 			type: type,
	// 			senderAddress,
	// 			subject,
	// 		};
	// 		console.log(data);
	// 		return await this.http
	// 			.post(`${this.apiServer}/sendCommunicationEmail`, data, { headers })
	// 			.toPromise();
	// 	} catch (err) {
	// 		console.error(err);
	// 		throw err;
	// 	}
	// }

	async resendConfirmation(company, bookingid: Number) {
		try {
			const headers = {
				'Content-Type': 'application/json',
				'x-company-id': `${company.id}`,
				clienttoken: `${this.blApiToken}`,
			};
			const body = {};
			return await this.http
				.put(
					`${this.blApi}/company/${company.id}/booking/${bookingid}/confirmation`,
					body,
					{ headers }
				)
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async getDays(
		company_id: number,
		service_id: number,
		start_date: String,
		end_date: String,
		resource: any,
		duration: any
	) {
		try {
			const headers = {
				'Content-Type': 'application/json',
				clienttoken: this.blApiToken,
			};
			let url = `${this.blApi}/company/${company_id}/service/${service_id}/days/${start_date}/${end_date}`;
			let join = '?';
			if (resource) {
				url = `${url}${join}resources=${resource}`;
				join = '&';
			}
			if (duration) {
				url = `${url}${join}duration=${duration}`;
			}
			return await this.http.get(`${url}`, { headers }).toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async searchVenue(company, query) {
		try {
			const headers = {
				'Content-Type': 'application/json',
				'x-company-id': `${company.id}`,
				clienttoken: `${this.blApiToken}`,
			};
			return await this.http
				.get(
					`${this.blApi
					}/company/${company}/venue-lookup?search=${query.toLowerCase()}`,
					{ headers }
				)
				.toPromise();
		} catch (error) {
			// return (error as Error).message;
			return error.message;
		}
	}

	async generateSSOToken(
		companyId: any,
		first_name: any,
		last_name: any,
		email: any
	) {
		try {
			const headers = {
				'Content-Type': 'application/json',
				clienttoken: `${this.blApiToken}`,
			};

			const bodyData = {
				first_name: `${first_name}`,
				last_name: `${last_name}`,
				email: `${email}`,
			};

			return await this.http
				.post(`${this.blApi}/company/${companyId}/sso`, bodyData, { headers })
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async ssoLogin(companyId: any, token: any) {
		try {
			const headers = {
				'Content-Type': 'application/json',
				clienttoken: `${this.blApiToken}`,
			};

			const bodyData = {
				token: `${token}`,
			};

			return await this.http
				.post(`${this.blApi}/company/${companyId}/sso/login`, bodyData, {
					headers,
				})
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async ssoLoginAdmin(companyId: any, token: any) {
		try {
			const headers = {
				'Content-Type': 'application/json',
				clienttoken: `${this.blApiToken}`,
			};

			const bodyData = {
				token: `${token}`,
			};

			return await this.http
				.post(`${this.blApi}/company/${companyId}/sso/login/admin`, bodyData, {
					headers,
				})
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async fees(companyId: any, date: string, serviceId: any, duration: number) {
		try {
			const headers = {
				'Content-Type': 'application/json',
				clienttoken: `${this.blApiToken}`,
			};

			const bodyData = {
				service_id: serviceId,
				date: `${date}`,
				duration: duration,
			};

			return await this.http
				.post(`${this.blApi}/company/${companyId}/fees`, bodyData, { headers })
				.toPromise();
		} catch (error) {
			// A 404 from /fees means "we could not price this", never a price. Do not
			// swallow it into a string: callers must see the failure, or they coerce
			// the missing fee to 0 and the customer checks out paying nothing.
			if (error instanceof HttpErrorResponse && error.status === 404) {
				console.warn(`fees: not priced for service ${serviceId} on ${date} —`, error.error?.message);
			}
			throw error;
		}
	}

	async linkExtraItem(
		companyId: any,
		serviceId: any,
		bookingId: any,
		itemId: string,
		extra: any,
		option?: string
	) {
		try {
			const headers = {
				'Content-Type': 'application/json',
				clienttoken: `${this.blApiToken}`,
			};

			const bodyData = {
				global_service_id: serviceId,
				booking_id: `${bookingId}`,
				item_id: `${itemId}`,
				data: extra,
			};

			let url = `${this.blApi}/company/${companyId}/extra-item/link`;
			if (option) {
				url = `${url}?op=${option}`;
			}

			return await this.http.post(url, bodyData, { headers }).toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async deleteExtraItemLink(
		companyId: any,
		serviceId: any,
		bookingId: any,
		itemId: string
	) {
		try {
			const headers = {
				'Content-Type': 'application/json',
				clienttoken: `${this.blApiToken}`,
			};

			return await this.http
				.delete(
					`${this.blApi}/company/${companyId}/extra-item/link/${itemId}/${bookingId}`,
					{ headers }
				)
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async getExtraItem(companyId: any, serviceId: any, itemId: string) {
		try {
			const headers = {
				'Content-Type': 'application/json',
				clienttoken: `${this.blApiToken}`,
			};

			return await this.http
				.get(
					`${this.blApi}/company/${companyId}/extra-item/${itemId}/service/${serviceId}`,
					{ headers }
				)
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async getServiceItemExtraLinksForBooking(
		companyId: number,
		bookingId: string,
		authToken: string,
		memberId: number
	) {
		try {
			const headers = {
				'Content-Type': 'application/json',
				clienttoken: `${this.blApiToken}`,
				authtoken: `${authToken}`,
				'x-member-id': `${memberId}`,
			};

			return await this.http
				.get(
					`${this.blApi}/company/${companyId}/extra-item/link/${bookingId}`,
					{ headers }
				)
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async payPurchase(
		companyId: number,
		serviceId: number,
		purchaseId: number,
		purchaseData: any,
		clientId: number
	) {
		try {
			let authToken: any = false;
			if (localStorage.getItem('auth_token')) {
				authToken = localStorage.getItem('auth_token');
			}
			const headers = {
				'Content-Type': 'application/json',
				clienttoken: `${this.blApiToken}`,
				'x-member-id': `${clientId}`
			};
			if (authToken) {
				headers['authtoken'] = `${authToken}`
			}

			return await this.http
				.put(
					`${this.blApi}/company/${companyId}/service/${serviceId}/purchase/${purchaseId}/pay`,
					purchaseData,
					{ headers }
				)
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async sendWelcomeEmail(companyId: any, memberId: any) {
		try {
			const headers = {
				'Content-Type': 'application/json',
				clienttoken: `${this.blApiToken}`,
			};

			const url = `${this.blApi}/company/${companyId}/member/${memberId}/send_welcome_email`;

			return await this.http.post(url, {}, { headers }).toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async bookingTimeline(
		companyId: number,
		purchaseid: any,
		memberid: any,
		memberEmail: any
	) {
		try {
			const headers = {
				'Content-Type': 'application/json',
				clienttoken: `${this.blApiToken}`,
			};

			const url = `${this.blApi}/company/${companyId}/auditlog/interactions`;
			const body = {
				data: {
					email_type: 'Welcome Email',
					from: 'notifications@bookinglab.co.uk',
					to: memberEmail,
					subject: 'Welcome to Westminster - a day to remember',
					email_body: '',
					content_type: 'text/html; charset=UTF-8',
				},
				total_id: purchaseid,
				client_id: memberid,
				interaction_type: 'email_sent',
			};

			return await this.http.post(url, body, { headers }).toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async submitCaseToOracle(
		company: any,
		booking: any,
		formSchema?: string,
		answers?: any[]
	) {
		const headers = {
			'Content-Type': 'application/json',
			'X-BL-TOKEN': `${this.token}`,
		};
		const body: any = {
			host: `${context.apiUrl}`,
			company_id: `${company.id}`,
			booking_id: booking.id,
			purchase_id: booking.purchase_id,
		};
		// Appointments run on the question-schema forms, so their answers are field slugs
		// carried by the app — booking.questions is {} on these bookings and bl-jrni-api
		// cannot read them from JRNI. Sending form_schema switches it to the appointments
		// payload (per-type Subject + Solihull's field map); omitting it leaves the
		// original ceremonies payload untouched.
		if (formSchema) {
			body.form_schema = formSchema;
			body.answers = Array.isArray(answers) ? answers : [];
		}

		return await this.http
			.post(`${this.apiServer}/oracle/recordIncident`, body, { headers })
			.toPromise();
	}

	async getIpAddress() {
		return this.http.get('https://api.ipify.org?format=json').toPromise();
	}

	// Ported from the previous Solihull appointments app (SRPO-169) for the
	// Civica eStore flow: payment audit logging and booking rollback on cancel.
	async logPaymentExtra(company, service, bookingId, extraObj) {
		const headers = { 'Content-Type': 'application/json', 'X-BL-TOKEN': `${this.token}` };

		const clientAdrr = await this.getIpAddress();

		const body = {
			host: `${context.apiUrl}`,
			company_id: `${company.id}`,
			service_id: `${service.id}`,
			order_id: `${bookingId}`,
			provider: 'civica',
			client_addr: `${clientAdrr['ip']}`,
			extra_obj: JSON.stringify(extraObj),
			type: 'extra',
		};
		return this.http.post(`${this.apiServer}/logPayment`, body, { headers }).toPromise();
	}

	async cancelMultiBookings(company_id, booking_id) {
		const headers = { 'Content-Type': 'application/json', 'X-BL-TOKEN': `${this.token}` };
		const body = {
			host: `${context.apiUrl}`,
			company_id: `${company_id}`,
			booking_id: `${booking_id}`,
		};

		return await this.http.post(`${this.apiServer}/cancelMultiBookings`, body, { headers }).toPromise();
	}

	async getFormSchema(formId: string) {
		try {
			const headers = {
				'Content-Type': 'application/json',
				clienttoken: `${this.blApiToken}`,
			};
			const data = {
				company: `${AppConfig.settings.core.company_id}`,
				client: `${AppConfig.settings.env.customer_name}`,
				form_name: `${formId}`,
			};
			return await this.http
				.post(`${this.blApi}/form/schema`, data, { headers })
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	createBasket(company: any): Observable<any> {
		let authToken: any = false;
		if (localStorage.getItem('auth_token')) {
			authToken = localStorage.getItem('auth_token');
		}
		const headers = {
			'Content-Type': 'application/json',
			clienttoken: `${this.blApiToken}`,
			'x-company-id': company.id,
		};
		if (authToken) {
			headers['authtoken'] = authToken;
		}

		const url = `${this.blApi}/basket-public`;

		return this.http
			.post<any>(
				url,
				{ company_id: company.id },
				{ headers, observe: 'response' }
			)
			.pipe(
				map((data) => data),
				catchError(this.handleError)
			);
	}

	/**
	 * Resolve the JRNI resource_id for a basket call.
	 *
	 * datastoreService.selectedResource is the source of truth when set — it is the resource
	 * the availability calls were filtered by — otherwise fall back to the candidates the
	 * caller supplied.
	 * Callers have historically passed the whole resource OBJECT rather than its id, and
	 * parseInt(object) is NaN — which serialises to null, and JRNI then silently assigns
	 * whatever resource is free at that slot, booking the customer into the wrong room.
	 * Accept an id or an object for each candidate, take the first usable one, and return
	 * null (never NaN, never 0) when there is genuinely no resource to send.
	 */
	public resolveResourceId(...candidates: any[]): number | null {
		const selected = this.datastoreService.selectedResource;
		const fromDatastore = Number(selected && typeof selected === 'object' ? selected.id : selected);
		if (fromDatastore > 0) {
			return fromDatastore;
		}
		for (const candidate of candidates) {
			const id = Number(candidate && typeof candidate === 'object' ? candidate.id : candidate);
			if (id > 0) {
				return id;
			}
		}
		return null;
	}

	async addServiceItem(
		company: any,
		basket: any,
		startTime: string,
		service: any,
		questions: any[],
		duration?: number,
		resource?: any
	) {
		try {
			console.log('auth token to use:', localStorage.getItem('auth_token'));
			const headers = {
				'Content-Type': 'application/json',
				clienttoken: `${this.blApiToken}`,
				'x-company-id': company.id,
				authtoken: `${localStorage.getItem('auth_token')}`,
			};

			const url = `${this.blApi}/baskets/${basket.id}/service_item-public`;

			const serviceItem: any = {
				company_id: company.id,
				service_id: service.id,
				start: `${startTime}`,
				resource_id: this.resolveResourceId(resource),
				// questions: questions,
			};
			if (duration) {
				serviceItem.duration = duration;
			}
			return await this.http.post(url, serviceItem, { headers }).toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async updateServiceItem(company: any, basket: any, serviceItem: any, body) {
		try {
			const headers = {
				'Content-Type': 'application/json',
				clienttoken: `${this.blApiToken}`,
				'x-company-id': company.id,
				authtoken: `${localStorage.getItem('auth_token')}`,
			};

			const url = `${this.blApi}/baskets/${basket.id}/service_item-public/${serviceItem.id}`;

			return await this.http.put(url, body, { headers }).toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async deleteServiceItem(company: any, basket: any, serviceItem: any) {
		try {
			const headers = {
				'Content-Type': 'application/json',
				clienttoken: `${this.blApiToken}`,
				'x-company-id': company.id,
				authtoken: `${localStorage.getItem('auth_token')}`,
			};

			const url = `${this.blApi}/baskets/${basket.id}/service_item-public/${serviceItem.id}`;

			return await this.http.delete(url, { headers }).toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async deleteBaskets(company: any) {
		try {
			const headers = {
				'Content-Type': 'application/json',
				clienttoken: `${this.blApiToken}`,
				'x-company-id': company.id,
				authtoken: `${localStorage.getItem('auth_token')}`,
			};

			const url = `${this.blApi}/basket-public`;

			return await this.http.delete(url, { headers }).toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async basketCheckout(company: any, basket: any, client: any) {
		try {
			const headers = {
				'Content-Type': 'application/json',
				clienttoken: `${this.blApiToken}`,
				'x-company-id': company.id,
				authtoken: `${localStorage.getItem('auth_token')}`,
			};

			const url = `${this.blApi}/baskets/${basket.id}/public-checkout`;
			const selectedSchema = this.datastoreService.selectedSchema;
			return await this.http
				.post(
					url,
					{
						company_id: company.id,
						client,
						settings: {
							status: "booked",
							"firstAvailableSlot": this.datastoreService.firstSlotAnalytic ? this.datastoreService.firstSlotAnalytic : "Null",
							questions: this.datastoreService.bookingAnswers,
							schema_revision: this.datastoreService.serviceQuestionSchema[selectedSchema].revision,
						}
					},
					{ headers }
				)
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async getAdministrators(company: any) {
		try {
			const headers = {
				'Content-Type': 'application/json',
				clienttoken: `${this.blApiToken}`,
				'x-company-id': company.id,
				authtoken: `${localStorage.getItem('auth_token')}`,
			};

			const url = `${this.blApi}/admin/login`;

			return await this.http.get(url, { headers }).toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async updateLocation(company: any) {
		try {
			const headers = {
				'Content-Type': 'application/json',
				'app-key': `${AppConfig.settings.core.app_key}`,
				'app-id': `${AppConfig.settings.core.app_id}`,
				'Auth-Token': `${localStorage.getItem('auth_token')}`,
			};

			const url = `${AppConfig.settings.apiServer.api_url}/api/${AppConfig.settings.apiServer.version}/login/admin`;

			return await this.http
				.put(url, { company_id: company.id }, { headers })
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	addCertificateOrder(
		certificates: Record<string, any>[] = [],
		postage: Record<string, any>,
		customerDetails: Record<string, any>,
		companyId: string,
		clientId: number,
		userRole: string,
		receipt: string | null
	): Observable<object> {
		const url = `${this.blApi}/company/${companyId}/order`;
		const headers = {
			'Content-Type': 'application/json',
			clienttoken: `${this.blApiToken}`,
		};
		const body = [];
		certificates.forEach((certificate) => {
			let cost = certificate.totalCost;
			let priorityInfo = undefined;
			let quantity = certificate.quantity;
			let price = certificate.certificateCost;
			let totalCost = certificate.totalCost;
			if (certificate.additionalCertificates?.cost) {
				cost = cost + certificate.additionalCertificates.cost;
				priorityInfo = {
					isPriorityOrder: true,
					priorityCost: certificate.certificateCost,
					additionalCertificatesCost: certificate.additionalCertificates.cost,
					numberAdditionalCertificates:
						certificate.additionalCertificates.quantity,
					priorityItemId: certificate.additionalCertificates.priorityItemId,
				};
				quantity = quantity + certificate.additionalCertificates.quantity;
				price = certificate.additionalCertificates.cost;
				totalCost = totalCost + certificate.additionalCertificates.total;
			}
			let createdBy = `${customerDetails.firstName} ${customerDetails.lastName}`;
			let createdByEmail = customerDetails.emailAddress;
			if (userRole === 'admin') {
				const agent = localStorage.getItem('agent_profile');
				if (agent) {
					createdBy = JSON.parse(agent).displayName;
					createdByEmail = JSON.parse(agent).mail;
				}
			}
			body.push({
				certificate_type: certificate.certificateType,
				certificate_number: certificate.id,
				item_id: certificate.itemId,
				price: price,
				postage: postage.cost,
				paid: totalCost,
				qty: quantity,
				data: {
					questions: certificate.certificateAnswers,
					postage: postage,
					priorityInfo: priorityInfo,
					userRole: userRole,
					receipt: receipt
				},
				client: {
					id: clientId,
				},
				created_by: createdBy,
				created_by_email: createdByEmail,
				address: customerDetails.address,
				shipping_address: !customerDetails.addressesSameCheck
					? customerDetails.address
					: customerDetails.deliveryAddress,
			});
		});

		return this.http
			.post(url, body, { headers })
			.pipe(
				catchError((err) =>
					throwError(() => new Error(err.error || err.message))
				)
			);
	}

	async createClient(
		companyId: string,
		parentId: string,
		clientData: {
			firstName: string;
			lastname: string;
			email: string;
			mobile: string;
		}
	): Promise<Record<string, any>> {
		const headers = {
			'Content-Type': 'application/json',
			'X-BL-TOKEN': `${this.token}`,
		};
		const body = {
			host: `${context.apiUrl}`,
			company_id: companyId,
			parent_id: parentId,
			client_data: {
				first_name: clientData.firstName,
				last_name: clientData.lastname,
				email: clientData.email,
				mobile: clientData.mobile
			},
		};

		return await firstValueFrom(
			this.http.post(`${this.apiServer}/createClient`, body, { headers })
		);
	}

	async submitOrderToCRM(companyId, orders) {
		try {
			const headers = {
				'Content-Type': 'application/json',
				clienttoken: `${this.blApiToken}`,
				'x-company-id': companyId,
			};

			const url = `${this.blApi}/company/${companyId}/order/submit`;

			return await this.http
				.post(
					url,
					{
						orders: orders,
					},
					{ headers }
				)
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	async submitApplication(
		applicationType: string,
		companyId: string,
		parentCompanyId: string,
		formData: Record<string, string>
	): Promise<Record<string, any>> {
		const headers = {
			'Content-Type': 'application/json',
			clienttoken: `${this.blApiToken}`,
		};

		const url = `${this.blApi}/formsubmissions/${applicationType}`;
		const client = await this.createClient(companyId, parentCompanyId, {
			firstName: formData['first_name'],
			lastname: formData['last_name'],
			email: formData['email'],
			mobile: formData['mobile']
		});

		const body = {
			customerId: client.id,
			status: 'PENDING',
			formData: formData,
		};

		return firstValueFrom(
			this.http
				.post(url, body, { headers })
				.pipe(
					catchError((err) =>
						throwError(() => new Error(err.error || err.message))
					)
				)
		).then((response) => {
			// Deliberately not awaited: a slow mail service should not hold up the
			// confirmation page. Caught so a mail failure cannot surface as an unhandled
			// rejection on a submission that actually succeeded.
			this.sendApplicationConfirmationEmail(formData['email']).catch((err) =>
				console.error('Confirmation email failed to send', err)
			);
			return response;
		});
	}

	async sendApplicationConfirmationEmail(
		email: string
	): Promise<Record<string, any>> {
		const url = `${this.blApi}/company/37001/email`;
		const headers = {
			'Content-Type': 'application/json',
			clienttoken: `${this.blApiToken}`,
		};
		const body = {
			slot: '37001',
			entity: 'company',
			to: email,
			from: 'notifications@bookinglab.co.uk',
			subject: 'Westminster City Council - Application received',
			type: 'wcc-applications-email',
		};

		return firstValueFrom(
			this.http
				.post(url, body, { headers })
				.pipe(
					catchError((err) =>
						throwError(() => new Error(err.error || err.message))
					)
				)
		);
	}

	async sendCertificateOrderConfirmationEmail(
		email: string
	): Promise<Record<string, any>> {
		const url = `${this.blApi}/company/37001/email`;
		const headers = {
			'Content-Type': 'application/json',
			clienttoken: `${this.blApiToken}`,
		};
		const body = {
			slot: '37001',
			entity: 'company',
			to: email,
			from: 'notifications@bookinglab.co.uk',
			subject: 'Westminster City Council - Certificate Order received',
			type: 'wcc-certificate-order-email',
		};

		return firstValueFrom(
			this.http
				.post(url, body, { headers })
				.pipe(
					catchError((err) =>
						throwError(() => new Error(err.error || err.message))
					)
				)
		);
	}

	async citizenshipAvailability(companyId: number, serviceId: number, date: string) {
		try {
			const headers = {
				'Content-Type': 'application/json',
				clienttoken: `${this.blApiToken}`,
			};

			return await this.http
				.get(
					`${this.blApi}/citizenship/availability/${companyId}/${serviceId}/${date}`,
					{ headers }
				)
				.toPromise();
		} catch (error) {
			return (error as Error).message;
		}
	}

	private handleError(res: HttpErrorResponse | any) {
		console.error(res.error || res.body.error);
		return throwError(() => new Error(res.error || res.body.error));
	}

	async emailTemplateSender(commsData: any) {
		try {
			const headers = {
				'Content-Type': 'application/json',
				clienttoken: `${this.blApiToken}`,
			};
			const url = `${this.blApi}/emailTemplateSender`;

			return firstValueFrom(
				this.http
					.post(url, commsData, { headers })
					.pipe(
						catchError((err) =>
							throwError(() => new Error(err.error || err.message))
						)
					)
			);
		} catch (error) {
			return (error as Error).message;
		}
	}
}

export class EmailType {
	host: String;
	companyid: String;
	slotid: String;
	recipient: String;
	type: String = 'CANCEL' || 'AMEND' || 'MOVED' || 'CONFIRMED';
	senderAddress: String;
	subject: String;
}
